<template>
  <div id="page-product-detail" class="page-products page-product-detail">
    <div class="page-title-box">
      <div class="page-title-right">
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb mb-0">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'home' }" href="#">Home</router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link
                :to="{
                  name: 'website-product-requests',
                  params: { site_id: currentWebsiteId },
                }"
                href="#"
                >Product Requests</router-link
              >
            </li>
            <li
              class="breadcrumb-item active"
              aria-current="page"
              v-text="pageTitle"
            ></li>
          </ol>
        </nav>
      </div>

      <div class="page-title-left">
        <h2 class="page-title" v-text="pageTitle"></h2>
      </div>
    </div>
    <div class="alert alert-danger" role="alert" v-if="error_message">
      {{ error_message }}
    </div>
    <div class="alert alert-success" role="alert" v-if="success">
      Product request was {{ successAction }}.
    </div>
    <div class="section-form card">
      <div class="card-body">
        <div class="form-group row mb-3">
          <label for class="col-2 col-form-label">Name</label>
          <div class="col-4">
            <input
              type="text"
              class="form-control"
              :class="[hasErrorInputClass('name')]"
              v-model="form.name"
              :disabled="controlDisabled"
            />
            <form-error-label
              :target="formErrors['name']"
              error-class="invalid-feedback"
              role="alert"
            ></form-error-label>
          </div>
        </div>
        <div class="form-group row mb-3">
          <label for class="col-2 col-form-label">Description</label>
          <div class="col-5">
            <textarea
              type="text"
              class="form-control"
              rows="5"
              :class="[hasErrorInputClass('description')]"
              v-model="form.description"
              :disabled="controlDisabled"
            ></textarea>
            <form-error-label
              :target="formErrors['description']"
              error-class="invalid-feedback"
              role="alert"
            ></form-error-label>
          </div>
        </div>
        <div class="form-group row mb-3">
          <label class="col-2">Photos</label>
          <div class="photos-container col-10">
            <image-input-component
              class="item"
              v-for="(angle, key) in angles"
              :key="key"
              v-model="form['upload_' + key + '_image']"
              :src-image="product_request['model_' + key + '_image']"
              @mark-deleted="form['delete_' + key + '_image'] = $event"
              :disabled="controlDisabled"
            >
              <span class="label" v-text="angle"></span>
              <template v-slot:delete="slotProps">
                <button
                  class="file-delete-button"
                  @click="slotProps.markDeleted(true)"
                >
                  <i class="dripicons-cross"></i>
                </button>
              </template>
            </image-input-component>
          </div>
        </div>
        <div class="mb-3" v-if="productRequestId">
          <div class="form-group row mb-1">
            <label for class="col-2 col-form-label">Updated Date</label>
            <div class="col-5">
              <span
                class="form-control-plaintext"
                v-text="getFormattedDate(product_request.updated_at)"
              ></span>
            </div>
          </div>
          <div class="form-group row mb-0" v-if="isSubmitted">
            <label for class="col-2 col-form-label">Submitted Date</label>
            <div class="col-5">
              <span
                class="form-control-plaintext"
                v-text="getFormattedDate(product_request.submitted_at)"
              ></span>
            </div>
          </div>
          <div class="form-group row mb-0" v-if="product_request.processed_at">
            <label for class="col-2 col-form-label">Processed Date</label>
            <div class="col-5">
              <span
                class="form-control-plaintext"
                v-text="getFormattedDate(product_request.processed_at)"
              ></span>
            </div>
          </div>
        </div>
        <div class="form-group form-actions mb-0">
          <template v-if="!isSubmitted">
            <button
              @click="save()"
              class="btn btn-secondary btn-save"
              :disabled="controlDisabled"
            >
              Save
            </button>
            <button
              @click="submit()"
              class="btn btn-primary btn-submit"
              :disabled="controlDisabled"
            >
              Submit
            </button>
            <button
              @click="revert()"
              class="btn btn-light btn-revert"
              :disabled="controlDisabled"
            >
              Revert
            </button>
          </template>
          <span
            class="spinner-border spinner-border-sm mr-1"
            role="status"
            aria-hidden="true"
            v-if="saving"
          ></span>

          <div class="destructive-actions">
            <button
              @click="destroy()"
              class="btn btn-danger btn-delete"
              v-if="!isSubmitted"
              :disabled="controlDisabled"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    <loading v-model="loading"></loading>
  </div>
</template>

<script>
import { cloneDeep, forEach } from "lodash";
import { mapState } from "vuex";
import { defineComponent } from "@vue/composition-api";

import moment from "moment";

import { EventBus } from "@/utils/EventBus";

import product_request from "@/client/api/product_request";

import FormErrorLabel from "@/ui/FormErrorLabel";
import ImageInputComponent from "@/ui/ImageInputComponent";
import Loading from "@/ui/Loading";

import useFormErrorHandler from "@/utils/FormErrorHandler";

export default defineComponent({
  components: {
    FormErrorLabel,
    ImageInputComponent,
    Loading,
  },
  data() {
    return {
      test: null,
      loading: false,
      saving: false,
      success: false,
      error_message: null,
      angles: {
        front: "Front",
        back: "Back",
        left: "Left",
        right: "Right",
        top: "Top",
        bottom: "Bottom",
      },
      product_request: {},
      form: {},
    };
  },
  setup() {
    let {
      formErrors,
      formShowErrors,
      formErrorMessage,
      resetFormError,
      handleFormError,
      hasError,
      hasErrorInputClass,
    } = useFormErrorHandler();

    return {
      formErrors,
      formErrorMessage,
      formShowErrors,
      resetFormError,
      handleFormError,
      hasError,
      hasErrorInputClass,
    };
  },
  watch: {
    $route: {
      handler: "loadData",
      immediate: true,
    },
    currentWebsiteId(val, oldVal) {
      if (oldVal != null && val != oldVal) {
        this.$router.replace({
          name: "website-product-requests",
          params: { site_id: val },
        });
      }
    },
  },
  methods: {
    async loadData() {
      if (this.productRequestId) {
        this.loading = true;

        let res = await product_request.getProductRequest(
          this.currentWebsiteId,
          this.productRequestId
        );

        if (res.data && res.data.data) {
          this.product_request = res.data.data;
        }

        this.loading = false;
      } else {
        this.product_request = {
          name: "",
          description: "",
        };
      }
      this.revert();
    },
    // selectAll(e) {
    //     if (this.isMobile) {
    //         e.target.setSelectionRange(0, e.target.value.length);
    //     } else {
    //         e.target.select();
    //     }
    // },
    async save(submit = false) {
      this.success = false;
      this.saving = true;
      this.error_message = null;
      this.resetFormError();
      let form = {
        submit,
        ...this.form,
      };
      try {
        if (this.productRequestId) {
          let res = await product_request.putProductRequest(
            this.currentWebsiteId,
            this.productRequestId,
            form
          );
          if (res.data && res.data.status == "ok") {
            this.success = true;
            this.loadData();
          }
        } else {
          let res = await product_request.postProductRequest(
            this.currentWebsiteId,
            form
          );
          if (res.data && res.data.status == "ok") {
            this.$router.replace({
              name: "website-product-requests-detail",
              params: {
                site_id: this.currentWebsiteId,
                product_request_id: res.data.id,
              },
            });
            this.success = true;
          }
        }
        setTimeout(() => {
          this.success = false;
        }, 5000);
      } catch (e) {
        console.log(e);
        this.handleFormError(e);
      }
      this.saving = false;
    },
    submit() {
      EventBus.$emit("show-modal", "confirm-modal", {
        title: "Confirmation",
        message: "Do you want to submit the request?",
        confirmCallback: () => {
          this.save(true);
        },
      });
    },
    revert() {
      this.$set(this, "form", cloneDeep(this.product_request));

      forEach(this.angles, (angle, key) => {
        this.$set(this.form, "upload_" + key + "_image", null);
        this.$set(this.form, "delete_" + key + "_image", false);
      });
    },
    getFormattedDate(date) {
      return moment(date).format("lll");
    },
    destroy() {
      EventBus.$emit("show-modal", "confirm-modal", {
        type: "danger",
        title: "Delete Confirmation",
        message: "Do you want to delete the request?",
        confirmLabel: "Delete",
        confirmCallback: this.doDestroy,
      });
    },
    async doDestroy() {
      this.saving = true;
      this.error_message = null;
      try {
        let res = await product_request.deleteProductRequest(
          this.currentWebsiteId,
          this.productRequestId
        );

        if (res.data && res.data.status == "ok") {
          console.log(res);
          this.$router.replace({
            name: "website-product-requests",
            params: {
              site_id: this.currentWebsiteId,
            },
          });
        }
      } catch (e) {
        let res = e.response;
        if (res.data && res.data.message) {
          // alert(res.data.message);
          this.error_message = res.data.message;
        }
      }
      this.saving = false;
    },
  },
  computed: {
    pageTitle() {
      if (this.productRequestId) {
        return (
          "Product Request: " +
          (this.product_request.name ? this.product_request.name : "(Untitled)")
        );
      } else {
        return "New Product Request";
      }
    },
    productRequestId() {
      return parseInt(this.$route.params.product_request_id);
    },
    isSubmitted() {
      return this.product_request.submitted_at != null;
    },
    successAction() {
      if (this.product_request.submitted_at != null) {
        return "submitted";
      }
      return "saved";
    },
    controlDisabled() {
      return this.loading || this.saving || this.isSubmitted;
    },
    ...mapState(["currentWebsiteId"]),
  },
});
</script>

<style lang="scss" scoped>
.split-view-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: rem-calc(20);

  @include ipad {
    grid-template-columns: 2fr 1fr;
  }

  .preview-image-container {
    order: 0;
    position: relative;
    padding-bottom: 100%;

    @include ipad {
      order: 1;
    }

    & > img {
      position: absolute;
      width: 100%;
      height: 100%;
      object-fit: cover;
      max-width: 100%;
    }
  }

  .form {
    order: 1;

    @include ipad {
      order: 0;
    }

    .html-code-panel {
      display: block;
      width: 100%;
      border: 1px solid #ccc;
      border-radius: rem-calc(5);
      padding: rem-calc(10);
      min-height: rem-calc(200);
    }
  }
}

.section-form {
  .photos-container::v-deep {
    display: grid;
    grid-template-columns: repeat(auto-fill, rem-calc(80));
    // grid-auto-rows: rem-calc(100);
    gap: rem-calc(20);

    & > .item {
      position: relative;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      .photo-frame {
        position: relative;
        width: rem-calc(80);
        height: rem-calc(80);
        border-radius: rem-calc(5);
        overflow: hidden;
        padding: rem-calc(5);
        border: 1px solid #cccccc;

        & > * {
          position: absolute;
          left: rem-calc(5);
          top: rem-calc(5);
          right: rem-calc(5);
          bottom: rem-calc(5);
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .preview-container,
        .file-input-container {
          margin-bottom: 0;
        }

        .file-input-container {
          cursor: pointer;

          &.empty {
            background-color: #efefef;
            background-position: center center;
            background-image: url("../img/icon-product.png");
            background-size: contain;
            overflow: hidden;

            @include retina {
              background-image: url("../img/icon-product@2x.png");
            }
          }

          & > .file-upload-input {
            opacity: 0;
          }
        }
      }

      .file-delete-button {
        position: absolute;
        width: rem-calc(20);
        height: rem-calc(20);
        right: rem-calc(-8);
        top: rem-calc(-8);
        background-color: $alert-color;
        color: white;
        border: 0;
        border-radius: rem-calc(10);
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }

      span.label {
        margin-top: rem-calc(4);
      }

      &.disabled {
        opacity: 0.8;

        .photo-frame {
          .file-input-container {
            cursor: initial;
          }
        }
      }

      // .btn-add {
      //     background-color: transparent;
      //     width: 100%;
      //     height: 100%;
      //     border: 0;
      //     font-size: rem-calc(40);
      //     line-height: 0;
      //     color: $lighten-secondary-color;
      //     transition: color 0.3s ease;

      //     &:hover {
      //         color: $secondary-color;
      //     }
      // }
    }
  }
}
</style>
